import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Center,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Image,
  Input,
  Link,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { Field, useFormikContext } from 'formik';
import { useDropzone } from 'react-dropzone';
import { MdClear } from 'react-icons/md';

import FormErrorMessage from './FormErrorMessage';
import MessageApi from '../../../../../api/message';
import { formFields } from '../consts/formConsts';
import {
  MediaHeaderTypeLabels,
  mediaAcceptedMimeTypesFor,
  mediaMaxSizeBytesFor,
  mediaMaxSizeMbFor,
  normalizeHeaderType,
} from '../consts/consts';

/**
 * Media (image / video / document) attached to a single campaign message.
 *
 * Rendered only for templates whose WhatsApp header carries media — see
 * FormWhatsappTemplateSelection. Uploading stores the file and fills in its public URL; a URL can
 * also be pasted directly. Either way the stored value is a URL, because WhatsApp fetches the file
 * from it at send time — anything behind auth or on a private bucket will fail to deliver.
 */
const FormMediaField = ({ messageIndex, headerType }) => {
  const { setFieldValue } = useFormikContext();

  const fieldName = `messageSequence.messages.${messageIndex}.mediaUrl`;
  const type = normalizeHeaderType(headerType);
  const mediaLabel =
    MediaHeaderTypeLabels[type] || formFields.messageSequence.messages.mediaUrl.label;

  // Meta caps images at 5MB but videos and documents at 16MB, so the limit shown and enforced
  // here depends on what the template's header actually carries.
  const maxSizeBytes = mediaMaxSizeBytesFor(type);
  const maxSizeMb = mediaMaxSizeMbFor(type);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const onDrop = useCallback(
    async (acceptedFiles, fileRejections) => {
      if (fileRejections?.length) {
        const isTooLarge = fileRejections[0]?.errors?.some((e) => e.code === 'file-too-large');
        setUploadError(
          isTooLarge
            ? `הקובץ גדול מדי — עד ${maxSizeMb}MB`
            : `סוג הקובץ אינו נתמך עבור ${mediaLabel}`,
        );
        return;
      }

      const file = acceptedFiles?.[0];
      if (!file) return;

      setUploadError('');
      setIsUploading(true);

      try {
        const { data } = await MessageApi.uploadCampaignMedia(file);
        setFieldValue(fieldName, data.url);
      } catch (error) {
        console.error('Failed to upload campaign media', error);
        setUploadError(error?.response?.data?.message || 'ההעלאה נכשלה, נסה שנית');
      } finally {
        setIsUploading(false);
      }
    },
    [fieldName, mediaLabel, maxSizeMb, setFieldValue],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: (mediaAcceptedMimeTypesFor(type) ?? []).join(', '),
    maxSize: maxSizeBytes,
    maxFiles: 1,
    multiple: false,
    disabled: isUploading,
  });

  return (
    <FormControl mt={4}>
      <FormLabel fontSize="sm">
        {`${formFields.messageSequence.messages.mediaUrl.label} (${mediaLabel})`}
      </FormLabel>

      <Field name={fieldName}>
        {({ field }) => (
          <>
            <Box
              {...getRootProps()}
              borderWidth="1px"
              borderStyle="dashed"
              borderColor={isDragActive ? 'teal.400' : 'gray.300'}
              background={isDragActive ? 'teal.50' : 'gray.50'}
              borderRadius="md"
              p={4}
              cursor={isUploading ? 'default' : 'pointer'}
            >
              <input {...getInputProps()} />
              <Center>
                {isUploading ? (
                  <HStack spacing={2}>
                    <Spinner size="sm" color="teal.500" />
                    <Text fontSize="sm" color="gray.600">
                      מעלה...
                    </Text>
                  </HStack>
                ) : (
                  <Text fontSize="sm" color="gray.600" textAlign="center">
                    {`גרור לכאן ${mediaLabel} או לחץ לבחירת קובץ (עד ${maxSizeMb}MB)`}
                  </Text>
                )}
              </Center>
            </Box>

            {uploadError && (
              <Text fontSize="xs" color="red.500" mt={1}>
                {uploadError}
              </Text>
            )}

            <Text fontSize="xs" color="gray.500" mt={3} mb={1}>
              או הדבק קישור ציבורי:
            </Text>
            <HStack alignItems="center">
              <Input
                {...field}
                value={field.value || ''}
                placeholder={formFields.messageSequence.messages.mediaUrl.placeholder}
                dir="ltr"
                isDisabled={isUploading}
              />
              <IconButton
                icon={<MdClear />}
                variant="outline"
                size="sm"
                aria-label={`הסר ${mediaLabel}`}
                isDisabled={!field.value || isUploading}
                onClick={() => {
                  setUploadError('');
                  setFieldValue(fieldName, '');
                }}
              />
            </HStack>

            <MediaPreview url={field.value} type={type} mediaLabel={mediaLabel} />
          </>
        )}
      </Field>

      <FormErrorMessage name={fieldName} />
    </FormControl>
  );
};

export default FormMediaField;

// ----------------------------------- Preview -----------------------------------
const MediaPreview = ({ url, type, mediaLabel }) => {
  const [hasLoadError, setHasLoadError] = useState(false);

  // A new URL deserves a fresh attempt at loading it
  useEffect(() => {
    setHasLoadError(false);
  }, [url]);

  if (!url) {
    return (
      <Text fontSize="xs" color="gray.500" mt={2}>
        {`התבנית דורשת ${mediaLabel}. וואטסאפ מוריד את הקובץ מהכתובת הזו בזמן השליחה.`}
      </Text>
    );
  }

  return (
    <Box mt={2}>
      {type === 'IMAGE' && !hasLoadError && (
        <Image
          src={url}
          alt={mediaLabel}
          maxH="120px"
          maxW="100%"
          objectFit="contain"
          borderWidth="1px"
          borderRadius="md"
          p={1}
          onError={() => setHasLoadError(true)}
        />
      )}
      {type === 'IMAGE' && hasLoadError && (
        <Text fontSize="xs" color="orange.500">
          לא ניתן לטעון תצוגה מקדימה מהקישור הזה. ודא שהקישור ציבורי ותקין.
        </Text>
      )}
      <Link href={url} isExternal fontSize="xs" color="teal.500" dir="ltr" display="block" mt={1}>
        {url}
      </Link>
    </Box>
  );
};
